import React, { useState, useEffect } from 'react';
import { Col, Form, Dropdown } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { Search, AlertCircle, Landmark, User, CreditCard } from 'lucide-react';
import { formatCurrency } from '../../utility';

const PaymentAccountSelect = ({ accountOwnerType, fieldName, options, placeholder, label, isDisabled }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { values, setFieldValue, touched, errors } = useFormikContext();
  const [showDropdown, setShowDropdown] = useState(false);

  // Sync initial search term if value already exists
  useEffect(() => {
    if (values[fieldName]) {
      const selected = (options || []).find(o => o.value === values[fieldName] || o._id === values[fieldName]);
      if (selected) {
        setSearchTerm(selected.label || selected.account_name);
      }
    } else {
      setSearchTerm("");
    }
  }, [values[fieldName], options]);

  const filteredAccounts = (options || []).filter(option => {
    const ownerType = option.accountOwnerType || option.account_owner_type;
    if (ownerType !== accountOwnerType) return false;

    const name = (option.label || option.account_name || "").toLowerCase();
    const cleanSearch = searchTerm.toLowerCase();
    return name.includes(cleanSearch);
  });

  return (
    <Col md={12}>
      <style>
        {`
            .erp-select-container { position: relative; }
            .premium-search-input {
                background: #f8fafc!important;
                border: 2px solid #e2e8f0!important;
                border-radius: 12px!important;
                padding: 12px 12px 12px 45px!important;
                font-weight: 700!important;
                font-size: 14px!important;
                color: #0f172a!important;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1)!important;
                width: 100%;
                outline: none;
            }
            .premium-search-input:focus {
                border-color: #001c3a!important;
                background: #fff!important;
                box-shadow: 0 0 0 4px rgba(0, 28, 58, 0.05)!important;
            }
            .search-icon-float {
                position: absolute;
                left: 16px;
                top: 50%;
                transform: translateY(-50%);
                color: #64748b;
                z-index: 5;
            }
            .erp-dropdown-menu {
                position: absolute;
                top: 100%;
                left: 0;
                width: 100%;
                max-height: 300px;
                overflow-y: auto;
                border-radius: 16px;
                border: 1px solid #e2e8f0;
                box-shadow: 0 15px 30px -5px rgba(0,0,0,0.1);
                margin-top: 8px;
                padding: 8px;
                background: white;
                z-index: 1050;
            }
            .erp-dropdown-item {
                padding: 12px 16px;
                border-radius: 10px;
                margin-bottom: 4px;
                transition: all 0.2s;
                border: 1px solid transparent;
                cursor: pointer;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            .erp-dropdown-item:hover {
                background: #f1f5f9;
                border-color: #e2e8f0;
            }
            .erp-dropdown-item.active {
                background: #001c3a;
                color: white;
            }
            .account-balance-tag {
                font-size: 10px;
                font-weight: 800;
                padding: 2px 10px;
                border-radius: 20px;
                text-transform: uppercase;
                white-space: nowrap;
            }
            .balance-positive { background: #dcfce7; color: #166534; }
            .balance-negative { background: #fee2e2; color: #991b1b; }
            .active .account-balance-tag { background: rgba(255,255,255,0.2); color: white; }
            
            .erp-label {
                font-size: 11px;
                font-weight: 800;
                color: #001c3a;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin-bottom: 10px;
                display: block;
            }
        `}
      </style>

      <div className="erp-select-container mb-3">
        {label && <label className="erp-label">{label}</label>}
        <div className="position-relative">
          <Search size={18} className="search-icon-float" />
          <input
            type="text"
            className="premium-search-input"
            placeholder={placeholder || "Search account..."}
            value={searchTerm}
            disabled={isDisabled}
            autoComplete="off"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
          />
        </div>

        {showDropdown && !isDisabled && (
          <div className="erp-dropdown-menu shadow-lg">
            {filteredAccounts.length > 0 ? (
              filteredAccounts.map((account) => {
                const accId = account.value || account._id;
                const accName = account.label || account.account_name;
                const balance = account.balance || 0;
                const isNegative = balance < 0;
                const ownerType = account.accountOwnerType || account.account_owner_type;

                return (
                  <div
                    key={accId}
                    className={`erp-dropdown-item ${values[fieldName] === accId ? 'active' : ''}`}
                    onClick={() => {
                      setFieldValue(fieldName, accId);
                      setSearchTerm(accName);
                      setShowDropdown(false);
                      if (ownerType === "Supplier") {
                        const supplierId = account.relatedField?.value || account.related_party_id;
                        if (supplierId) setFieldValue("references.supplier", supplierId);
                      }
                    }}
                  >
                    <div>
                      <div className="fw-bold fs-7">{accName}</div>
                      <div className={`x-small opacity-75 ${values[fieldName] === accId ? 'text-white' : 'text-muted'}`}>
                        {ownerType === 'Company' ? 'Treasury' : ownerType}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className={`account-balance-tag ${isNegative ? 'balance-negative' : 'balance-positive'}`}>
                        {isNegative ? 'Owed: ' : 'Bal: '}
                        {formatCurrency(Math.abs(balance))}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-3 text-center text-muted small">No accounts discovered</div>
            )}
          </div>
        )}

        {touched[fieldName] && errors[fieldName] && (
          <div className="text-danger x-small fw-bold mt-2 d-flex align-items-center gap-1">
            <AlertCircle size={12} /> {errors[fieldName]}
          </div>
        )}
      </div>

      {/* Click outside to close - Simplified Modal-like backdrop */}
      {showDropdown && <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 999 }} onClick={() => setShowDropdown(false)}></div>}
    </Col>
  );
};

export default PaymentAccountSelect;
